import React, { useState, useEffect, useRef, useCallback } from 'react';
import payCardOpsService from '../services/payCardOpsService';
import roomService from '../services/roomService ';
import Sidebar from './Sidebar';

/* ─── Constants ─────────────────────────────────────────────────────────── */
const LAUNDRY_SVC = { both: { label: 'Wash + Press', rate: 20 } };
const LAUNDRY_DEFAULTS = ['T-shirts / Tops', 'Shirts', 'Trousers / Jeans', 'Bedsheets / Towels'];
const DEFAULT_CANTEEN = [
  { id: 'tea',     name: 'Tea',          emoji: '☕', price: 15 },
  { id: 'coffee',  name: 'Coffee',       emoji: '☕', price: 25 },
  { id: 'biscuit', name: 'Biscuits',     emoji: '🍪', price: 20 },
  { id: 'bread',   name: 'Bread Butter', emoji: '🍞', price: 30 },
  { id: 'maggi',   name: 'Maggi',        emoji: '🍜', price: 50 },
  { id: 'samosa',  name: 'Samosa',       emoji: '🥟', price: 15 },
  { id: 'vada',    name: 'Vada Pav',     emoji: '🍔', price: 20 },
  { id: 'juice',   name: 'Juice',        emoji: '🧃', price: 40 },
  { id: 'water',   name: 'Water Bottle', emoji: '💧', price: 20 },
  { id: 'chips',   name: 'Chips',        emoji: '🍟', price: 25 },
  { id: 'dal',     name: 'Dal Rice',     emoji: '🍛', price: 80 },
  { id: 'roti',    name: 'Roti Sabzi',   emoji: '🫓', price: 60 },
];

/* ─── Styles ─────────────────────────────────────────────────────────────── */
const C = {
  navy:   '#1B3B5F', deep:  '#0D1F2D', gold:  '#D4AF37',
  cream:  '#F8F6F3', light: '#E8E4DF', mid:   '#C4BEB8',
  text:   '#2C2C2C', sub:   '#6B6B6B', green: '#2D5F4F',
  red:    '#DC3545', amber: '#F59E0B', coral: '#E85D4A',
};
const card   = { background:'white', borderRadius:14, boxShadow:'0 4px 20px rgba(0,0,0,0.08)', overflow:'hidden' };
const lbl    = { fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, marginBottom:6, display:'block' };
const inp    = { padding:'12px 14px', border:`2px solid ${C.light}`, borderRadius:8, fontFamily:'Work Sans,sans-serif', fontSize:14, outline:'none', width:'100%', boxSizing:'border-box' };
const btnNav = (active) => ({ padding:'13px 22px', border:`2px solid ${active ? C.navy : C.light}`, background: active ? C.navy : 'white', color: active ? 'white' : C.sub, borderRadius:10, fontFamily:'Work Sans,sans-serif', fontWeight:700, fontSize:13, textTransform:'uppercase', letterSpacing:1, cursor:'pointer', display:'flex', alignItems:'center', gap:8 });
const btnCharge = (disabled) => ({ flex:1, padding:'15px', background: disabled ? C.mid : C.navy, color:'white', border:'none', borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, fontSize:13, textTransform:'uppercase', letterSpacing:1, cursor: disabled ? 'not-allowed' : 'pointer' });

/* ─── Toast ──────────────────────────────────────────────────────────────── */
const Toast = ({ msg, visible }) => (
  <div style={{ position:'fixed', bottom:36, right:36, background:C.navy, color:'white', padding:'16px 24px', borderRadius:10, fontSize:14, fontWeight:600, boxShadow:'0 8px 30px rgba(0,0,0,0.2)', transform: visible ? 'translateY(0)' : 'translateY(120px)', opacity: visible ? 1 : 0, transition:'all 0.35s ease', zIndex:9999, display:'flex', alignItems:'center', gap:10 }}>
    {msg}
  </div>
);

/* ─── Scan Card (left panel) ─────────────────────────────────────────────── */
const ScanCard = ({ module, student, onScan, onClear, chargeResult }) => {
  const [input, setInput] = useState('');
  const inputRef = useRef(null);
  useEffect(() => { if (inputRef.current) inputRef.current.focus(); }, [module]);

  const doScan = () => { onScan(input); setInput(''); };

  return (
    <div style={{ ...card, position:'sticky', top:24 }}>
      <div style={{ background:C.navy, color:'white', padding:'22px 26px' }}>
        <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, fontWeight:400 }}>Scan Student Card</div>
        <div style={{ fontSize:12, opacity:0.7, marginTop:3 }}>Point scanner at QR or enter code manually</div>
      </div>
      <div style={{ padding:'20px 26px', borderBottom:`1px solid ${C.light}` }}>
        <label style={lbl}>QR / Card Code</label>
        <div style={{ display:'flex', gap:8 }}>
          <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && doScan()}
            placeholder="Enter student ID or QR code"
            style={{ ...inp, flex:1 }} />
          <button onClick={doScan} style={{ padding:'12px 14px', background:C.navy, color:'white', border:'none', borderRadius:8, cursor:'pointer', fontSize:18 }}>⊞</button>
        </div>
      </div>

      {!student ? (
        <div style={{ padding:'36px 26px', textAlign:'center', color:C.sub }}>
          <div style={{ fontSize:48, opacity:0.3, marginBottom:12 }}>⊞</div>
          <p style={{ fontSize:13, lineHeight:1.6 }}>Scan a student QR code<br/>or enter the student ID above</p>
        </div>
      ) : (
        <div style={{ padding:'20px 26px' }}>
          <div style={{ display:'flex', alignItems:'center', gap:14, marginBottom:16 }}>
            <div style={{ width:52, height:52, borderRadius:10, background:`linear-gradient(135deg,${C.gold},#C19A2E)`, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Cormorant Garamond,serif', fontSize:22, fontWeight:700, color:C.navy, flexShrink:0 }}>
              {student.name.charAt(0)}
            </div>
            <div>
              <div style={{ fontSize:16, fontWeight:700, color:C.navy }}>{student.name}</div>
              <div style={{ fontSize:12, color:C.sub, marginTop:2 }}>Student ID: {student.admissionNumber || student.studentId} · Room {student.roomCode || '—'}</div>
            </div>
          </div>
          {/* ✅ Show suspended message when balance is ₹0 */}
          {student.balance <= 0 && (
            <div style={{ background:'#FEE2E2', border:'1px solid #E85D4A', borderRadius:8, padding:'12px 16px', marginBottom:12, display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ fontSize:20 }}>🚫</span>
              <div>
                <div style={{ fontWeight:700, color:'#E85D4A', fontSize:13 }}>Pay Card Suspended</div>
                <div style={{ fontSize:12, color:'#6B6B6B', marginTop:2 }}>Balance is ₹0. Please recharge the wallet to re-activate.</div>
              </div>
            </div>
          )}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', background: student.balance <= 0 ? '#F8F6F3' : student.balance < 2000 ? '#FFF3CD' : C.cream, borderRadius:10, padding:'14px 16px', marginBottom:8, opacity: student.balance <= 0 ? 0.6 : 1 }}>
            <span style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub }}>Wallet Balance</span>
            <span style={{ fontFamily:'Cormorant Garamond,serif', fontSize:26, fontWeight:700, color: student.balance < 2000 ? C.red : C.navy }}>₹{Number(student.balance).toLocaleString('en-IN')}</span>
          </div>
          {chargeResult && (
            <div style={{ background:'#D4EDDA', borderRadius:10, padding:'14px 16px', display:'flex', alignItems:'center', gap:12, marginTop:8 }}>
              <span style={{ fontSize:24 }}>✓</span>
              <div>
                <div style={{ fontWeight:700, fontSize:14, color:'#155724' }}>Charged Successfully</div>
                <div style={{ fontSize:12, color:'#1e7e34', marginTop:2 }}>{chargeResult}</div>
              </div>
            </div>
          )}
          <button onClick={onClear} style={{ marginTop:12, width:'100%', padding:'8px', background:'transparent', border:`1px solid ${C.light}`, borderRadius:6, cursor:'pointer', fontSize:12, color:C.sub }}>✕ Clear Student</button>
        </div>
      )}
    </div>
  );
};

/* ─── CANTEEN MODULE ─────────────────────────────────────────────────────── */
const CanteenModule = ({ student, setStudent, showToast, items: propItems }) => {
  const [localItems, setLocalItems] = React.useState(propItems || DEFAULT_CANTEEN);
  // Sync when parent canteenItems updates (after saving from RatesMgmtModal)
  React.useEffect(() => {
    if (propItems && propItems.length > 0) setLocalItems(propItems);
  }, [propItems]);
  // Also fetch fresh items from API on mount
  React.useEffect(() => {
    payCardOpsService.getCanteenItems()
      .then(items => { if (items?.length > 0) setLocalItems(items); })
      .catch(() => {});
  }, []);
 const [search, setSearch] = useState('');
const [cart, setCart] = useState({});
const [chargeResult, setChargeResult] = useState('');
const [showVisitor, setShowVisitor] = useState(false);
const [visitorMode, setVisitorMode] = useState('CASH');

// Filter items by name as the user types
const items = !search.trim()
  ? localItems
  : localItems.filter(i => i.name.toLowerCase().includes(search.trim().toLowerCase()));

  const total = Object.values(cart).reduce((s, i) => s + i.price * i.qty, 0);

  const toggleItem = (item) => {
    setCart(c => {
      const n = { ...c };
      if (n[item.id]) delete n[item.id];
      else n[item.id] = { ...item, qty: 1 };
      return n;
    });
  };
  const changeQty = (id, delta) => setCart(c => ({ ...c, [id]: { ...c[id], qty: Math.max(1, c[id].qty + delta) } }));

  const charge = async () => {
    if (!student || total === 0) return;
    try {
      const cartItems = Object.values(cart).map(i => ({ name: i.name, qty: i.qty, price: i.price }));
      const res = await payCardOpsService.chargeCanteen(student.payCardId, cartItems);
      const newBal = res.balanceAfter != null ? res.balanceAfter : (student.balance - total);
      setStudent(s => ({ ...s, balance: newBal }));
      setChargeResult(`₹${total.toLocaleString('en-IN')} deducted · New Balance: ₹${newBal.toLocaleString('en-IN')}`);
      showToast(`✓ ₹${total.toLocaleString('en-IN')} charged to ${student.name}`);
      setCart({});
    } catch(e) {
      showToast('❌ ' + (e.response?.data?.message || 'Charge failed'));
    }
  };
  const recordVisitorSale = async () => {
  if (total === 0) { showToast('❌ Add items first'); return; }
  try {
    const itemsText = Object.values(cart)
      .map(i => `${i.name} x${i.qty}`).join(', ');
    await payCardOpsService.recordVisitorSale({
      items: itemsText,
      amount: total,
      paymentMode: visitorMode,
      operatorName: 'Staff',
    });
    showToast(`✓ Visitor sale ₹${total.toLocaleString('en-IN')} (${visitorMode}) recorded`);
    setCart({});
    setShowVisitor(false);
  } catch(e) {
  console.error('Visitor sale error:', e.response?.status, e.response?.data, e);
  showToast('❌ ' + (e.response?.data?.message || e.response?.status || 'Sale failed'));
}
};

  const handleScanStudent = useCallback(async (idStr) => {
    if (!idStr.trim()) return;
    setChargeResult('');
    try {
      const data = await payCardOpsService.lookupStudent(idStr.trim());
      if (data.cardStatus === 'NOT_ISSUED') { showToast('❌ No pay card issued for this student'); return; }
      if (data.cardStatus === 'INACTIVE')  { showToast('⏳ Pay Card fee payment pending. Student must pay prepaid card fee to activate.'); return; }
      if (data.cardStatus === 'BLOCKED')   { showToast('🚫 Pay Card is suspended — balance is ₹0. Please recharge the wallet first.'); return; }
      setStudent({ id: data.payCardId, payCardId: data.payCardId, admissionId: data.admissionId, studentId: data.studentId, admissionNumber: data.admissionNumber, name: data.studentName, roomCode: data.bedCode, balance: data.balance, status: data.status });
    } catch(e) {
      showToast('❌ Student not found: ' + idStr);
    }
  }, [showToast]);

  return (
    <>
    <div style={{ display:'grid', gridTemplateColumns:'320px 1fr', gap:28, alignItems:'start' }}>
      <ScanCard module="canteen" student={student} onScan={handleScanStudent} onClear={() => { setStudent(null); setChargeResult(''); }} chargeResult={chargeResult} />
      <div style={card}>
        <div style={{ padding:'20px 28px', borderBottom:`2px solid ${C.light}`, display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:22 }}>🍔</span>
          <span style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, fontWeight:600, color:C.navy }}>Select Items</span>
        </div>
        <div style={{ padding:28 }}>
      {/* Search */}
      <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:18 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search item e.g. Paratha, Sandwich, Tea…"
          style={{ ...inp, flex:1 }}
        />
        {search && (
          <button onClick={() => setSearch('')}
            style={{ padding:'11px 16px', background:C.cream, border:`2px solid ${C.light}`, borderRadius:8, fontFamily:'Work Sans,sans-serif', fontSize:13, fontWeight:700, cursor:'pointer', color:C.sub, whiteSpace:'nowrap' }}>
            ✕ Clear
          </button>
        )}
      </div>
      <div style={{ fontSize:12, color:C.sub, marginBottom:12 }}>
        {items.length} item{items.length !== 1 ? 's' : ''}
        {search.trim() && ` matching "${search.trim()}"`}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(120px,1fr))', gap:10, marginBottom:28 }}>
        {items.map(item => {
              const sel = !!cart[item.id];
              return (
                <div key={item.id} onClick={() => toggleItem(item)}
                  style={{ border:`2px solid ${sel ? C.navy : C.light}`, borderRadius:10, padding:'14px 10px', cursor:'pointer', textAlign:'center', background: sel ? '#EEF3F9' : 'white', transition:'all 0.15s' }}>
                  <div style={{ fontSize:24, marginBottom:6 }}>{item.emoji}</div>
                  <div style={{ fontSize:12, fontWeight:700, color:C.text, marginBottom:3 }}>{item.name}</div>
                  <div style={{ fontSize:13, fontWeight:700, color:C.navy, fontFamily:'Cormorant Garamond,serif' }}>₹{item.price}</div>
                  {sel && (
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:8 }}>
                      <button onClick={e => { e.stopPropagation(); changeQty(item.id, -1); }} style={{ width:22, height:22, border:`2px solid ${C.navy}`, background:'white', borderRadius:'50%', cursor:'pointer', fontSize:14, fontWeight:700, color:C.navy, display:'flex', alignItems:'center', justifyContent:'center' }}>−</button>
                      <span style={{ fontSize:13, fontWeight:700 }}>{cart[item.id].qty}</span>
                      <button onClick={e => { e.stopPropagation(); changeQty(item.id, 1); }} style={{ width:22, height:22, border:`2px solid ${C.navy}`, background:'white', borderRadius:'50%', cursor:'pointer', fontSize:14, fontWeight:700, color:C.navy, display:'flex', alignItems:'center', justifyContent:'center' }}>+</button>
                    </div>
                  )}
                </div>
             );
        })}
      </div>

      {items.length === 0 && (
        <div style={{ textAlign:'center', padding:'30px 0', color:C.sub, fontSize:13, marginTop:-18, marginBottom:28 }}>
          No items match "{search}"
        </div>
      )}

      {/* Order Summary */}

          {/* Order Summary */}
          <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1.5, color:C.sub, marginBottom:12 }}>Order Summary</div>
          <div style={{ background:C.cream, borderRadius:10, padding:18, marginBottom:20 }}>
            {Object.values(cart).length === 0 ? (
              <div style={{ textAlign:'center', color:C.sub, fontSize:13, padding:'8px 0' }}>No items selected yet</div>
            ) : (
              <>
                {Object.values(cart).map(i => (
                  <div key={i.id} style={{ display:'flex', justifyContent:'space-between', fontSize:14, padding:'6px 0', borderBottom:`1px solid ${C.light}` }}>
                    <span>{i.emoji} {i.name} × {i.qty}</span>
                    <span>₹{(i.price * i.qty).toLocaleString('en-IN')}</span>
                  </div>
                ))}
                <div style={{ display:'flex', justifyContent:'space-between', fontFamily:'Cormorant Garamond,serif', fontSize:20, fontWeight:700, color:C.navy, paddingTop:12, marginTop:4, borderTop:`2px solid ${C.mid}` }}>
                  <span>Total</span><span>₹{total.toLocaleString('en-IN')}</span>
                </div>
              </>
            )}
          </div>
          <div style={{ display:'flex', gap:12 }}>
           <button onClick={() => setCart({})} style={{ padding:'14px 20px', background:C.light, color:C.sub, border:'none', borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, fontSize:13, cursor:'pointer' }}>Clear</button>
        <button onClick={() => { if (total === 0) { showToast('❌ Add items first'); return; } setShowVisitor(true); }} style={{ padding:'14px 20px', background:'white', color:C.navy, border:`2px solid ${C.navy}`, borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, fontSize:13, cursor:'pointer', whiteSpace:'nowrap' }}>Visitor Sale</button>
        <button onClick={charge} disabled={!student || total === 0} style={btnCharge(!student || total === 0)}>Charge Wallet</button>
          </div>
        </div>

        {showVisitor && (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'white', borderRadius:14, maxWidth:400, width:'100%', overflow:'hidden' }}>
        <div style={{ padding:'18px 24px', borderBottom:`1px solid ${C.light}` }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, color:C.navy }}>Visitor Sale</div>
          <div style={{ fontSize:12, color:C.sub }}>Walk-in customer · no card needed</div>
        </div>
        <div style={{ padding:'18px 24px' }}>
          {Object.values(cart).map(i => (
            <div key={i.id} style={{ display:'flex', justifyContent:'space-between', fontSize:14, padding:'5px 0' }}>
              <span>{i.emoji} {i.name} × {i.qty}</span>
              <span>₹{(i.price * i.qty).toLocaleString('en-IN')}</span>
            </div>
          ))}
          <div style={{ display:'flex', justifyContent:'space-between', fontFamily:'Cormorant Garamond,serif', fontSize:20, fontWeight:700, color:C.navy, paddingTop:10, marginTop:6, borderTop:`2px solid ${C.mid}` }}>
            <span>Total</span><span>₹{total.toLocaleString('en-IN')}</span>
          </div>

          <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, margin:'16px 0 8px' }}>Payment mode</div>
          <div style={{ display:'flex', gap:8 }}>
            {['CASH','UPI'].map(m => (
              <button key={m} onClick={() => setVisitorMode(m)}
                style={{ flex:1, padding:'10px', borderRadius:8, cursor:'pointer', fontSize:13, fontWeight:700,
                  border:`2px solid ${visitorMode===m ? C.navy : C.light}`,
                  background: visitorMode===m ? '#EEF3F9' : 'white',
                  color: visitorMode===m ? C.navy : C.sub }}>
                {m === 'CASH' ? 'Cash' : 'UPI'}
              </button>
            ))}
          </div>
        </div>
        <div style={{ padding:'16px 24px', background:C.cream, borderTop:`1px solid ${C.light}`, display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button onClick={() => setShowVisitor(false)} style={{ padding:'10px 20px', background:C.light, color:C.text, border:'none', borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, cursor:'pointer' }}>Cancel</button>
          <button onClick={recordVisitorSale} style={{ padding:'10px 20px', background:C.navy, color:'white', border:'none', borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, cursor:'pointer' }}>Record Sale</button>
        </div>
      </div>
    </div>
  )}
      </div>
    </div>
    </>
  );
};

/* ─── LAUNDRY MODULE ─────────────────────────────────────────────────────── */
const LaundryModule = ({ student, setStudent, showToast, rates, initialRows, setGlobalRows }) => {
  // Laundry items now carry their OWN price, loaded from the API
const [rows, setRows] = useState([]);   // [{id,name,price,qty}]
const [chargeResult, setChargeResult] = useState('');
const [showReceipt, setShowReceipt] = useState(null);

// Load laundry items (name + per-item price) from the API
React.useEffect(() => {
  payCardOpsService.getLaundryItems()
    .then(list => {
      const arr = (list && list.length > 0)
        ? list.map(i => ({ id: i.id, name: i.name, price: i.price, qty: 0 }))
        : [];
      setRows(arr);
    })
    .catch(() => setRows([]));
}, []);

const total = rows.reduce((s, r) => r.qty > 0 ? s + r.qty * (r.price || 0) : s, 0);

const changeQty = (id, d) => setRows(rs => rs.map(r => r.id === id ? { ...r, qty: Math.max(0, r.qty + d) } : r));

  const charge = async () => {
    if (!student || total === 0) return;
    const active = rows.filter(r => r.qty > 0);
  try {
    const items = active.map(r => ({ name: r.name, qty: r.qty, price: r.price, amount: r.qty * (r.price || 0) }));
    const res = await payCardOpsService.chargeLaundry(student.payCardId, items);
    const newBal = res.balanceAfter != null ? res.balanceAfter : (student.balance - total);
    setStudent(s => ({ ...s, balance: newBal }));
    setChargeResult(`₹${total.toLocaleString('en-IN')} deducted · New Balance: ₹${newBal.toLocaleString('en-IN')}`);
    showToast(`✓ Laundry ₹${total.toLocaleString('en-IN')} charged to ${student.name}`);
    setShowReceipt({ student: { ...student, balance: newBal }, items: active, total, newBal });
    setRows(rs => rs.map(r => ({ ...r, qty: 0 })));
    } catch(e) {
      showToast('❌ ' + (e.response?.data?.message || 'Charge failed'));
    }
  };

  const handleScanStudent = useCallback(async (idStr) => {
    if (!idStr.trim()) return;
    setChargeResult('');
    try {
      const data = await payCardOpsService.lookupStudent(idStr.trim());
      if (data.cardStatus === 'NOT_ISSUED') { showToast('❌ No pay card issued'); return; }
      if (data.cardStatus === 'INACTIVE')  { showToast('⏳ Pay Card fee payment pending. Student must pay prepaid card fee to activate.'); return; }
      if (data.cardStatus === 'BLOCKED')   { showToast('🚫 Pay Card is suspended — balance is ₹0. Please recharge the wallet first.'); return; }
      if (data.cardStatus === 'BLOCKED')    { showToast('❌ Pay card is blocked'); return; }
      setStudent({ id: data.payCardId, payCardId: data.payCardId, admissionId: data.admissionId, studentId: data.studentId, admissionNumber: data.admissionNumber, name: data.studentName, roomCode: data.bedCode, balance: data.balance, status: data.status });
    } catch(e) {
      showToast('❌ Student not found: ' + idStr);
    }
  }, [showToast]);

  return (
    <>
      <div style={{ display:'grid', gridTemplateColumns:'320px 1fr', gap:28, alignItems:'start' }}>
        <ScanCard module="laundry" student={student} onScan={handleScanStudent} onClear={() => { setStudent(null); setChargeResult(''); }} chargeResult={chargeResult} />
        <div style={card}>
          <div style={{ padding:'20px 28px', borderBottom:`2px solid ${C.light}`, display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:22 }}>👕</span>
            <span style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, fontWeight:600, color:C.navy }}>Laundry Service</span>
          </div>
          <div style={{ padding:28 }}>
            {/* Info line */}
        <div style={{ fontSize:13, color:C.sub, marginBottom:20 }}>
          Each item is charged at its own rate. Set quantities below, then charge.
        </div>

            {/* Items table */}
            <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:8 }}>
              <thead>
                <tr style={{ background:C.cream }}>
                  {['Item','Qty','Rate','Amount'].map((h, i) => (
                <th key={h} style={{ padding:'10px 12px', textAlign: i === 2 ? 'center' : i === 3 ? 'right' : 'left', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, borderBottom:`2px solid ${C.light}` }}>{h}</th>
              ))}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
              const amt = row.qty > 0 ? row.qty * (row.price || 0) : 0;
              return (
                <tr key={row.id} style={{ borderBottom:`1px solid ${C.light}` }}>
                  <td style={{ padding:'12px 12px', fontSize:14 }}>
                    <span style={{ fontWeight:600 }}>{row.name}</span>
                  </td>
                  <td style={{ padding:'12px 12px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <button onClick={() => changeQty(row.id, -1)} style={{ width:24, height:24, border:`1.5px solid ${C.mid}`, background:'white', borderRadius:'50%', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center' }}>−</button>
                      <span style={{ fontSize:14, fontWeight:700, minWidth:20, textAlign:'center' }}>{row.qty}</span>
                      <button onClick={() => changeQty(row.id, 1)} style={{ width:24, height:24, border:`1.5px solid ${C.mid}`, background:'white', borderRadius:'50%', cursor:'pointer', fontSize:14, display:'flex', alignItems:'center', justifyContent:'center' }}>+</button>
                    </div>
                  </td>
                  <td style={{ padding:'12px 12px', textAlign:'center', fontSize:13, fontWeight:700, color:C.sub }}>
                    ₹{row.price}/item
                  </td>
                  <td style={{ padding:'12px 12px', textAlign:'right', fontFamily:'Cormorant Garamond,serif', fontSize:17, fontWeight:700, color: amt > 0 ? C.navy : C.mid }}>
                    {amt > 0 ? `₹${amt.toLocaleString('en-IN')}` : '—'}
                  </td>
                </tr>
              );
            })}
              </tbody>
            </table>

            {/* Summary */}
            <div style={{ background:C.cream, borderRadius:10, padding:18, marginBottom:20 }}>
              {rows.filter(r => r.qty > 0).length === 0 ? (
            <div style={{ color:C.sub, fontSize:13, fontStyle:'italic' }}>Select a quantity for each item</div>
          ) : (
            rows.filter(r => r.qty > 0).map(r => (
              <div key={r.id} style={{ display:'flex', justifyContent:'space-between', fontSize:14, padding:'6px 0', borderBottom:`1px solid ${C.light}` }}>
                <span>{r.name} × {r.qty}</span>
                <span>₹{(r.qty * (r.price || 0)).toLocaleString('en-IN')}</span>
              </div>
            ))
          )}
              <div style={{ display:'flex', justifyContent:'space-between', fontFamily:'Cormorant Garamond,serif', fontSize:20, fontWeight:700, color:C.navy, paddingTop:12, marginTop:4, borderTop:`2px solid ${C.mid}` }}>
                <span>Total Charge</span><span>₹{total.toLocaleString('en-IN')}</span>
              </div>
            </div>
            <div style={{ display:'flex', gap:12 }}>
<button onClick={() => setRows(rs => rs.map(r => ({ ...r, qty: 0 })))} style={{ padding:'14px 20px', background:C.light, color:C.sub, border:'none', borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, fontSize:13, cursor:'pointer' }}>Clear All</button>              <button onClick={charge} disabled={!student || total === 0} style={btnCharge(!student || total === 0)}>Charge Wallet</button>
            </div>
          </div>
        </div>
      </div>

      {/* Receipt Modal */}
      {showReceipt && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'white', borderRadius:14, maxWidth:520, width:'100%', overflow:'hidden' }}>
            <div id="printReceipt" style={{ padding:'28px 32px' }}>
              <div style={{ textAlign:'center', marginBottom:18, paddingBottom:14, borderBottom:`2px solid ${C.light}` }}>
                <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:26, letterSpacing:4, color:C.navy, fontWeight:300 }}>OMKARA</div>
                <div style={{ fontSize:11, letterSpacing:2, textTransform:'uppercase', color:C.sub, marginTop:2 }}>Hostel Portal</div>
                <div style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:1.5, color:C.navy, marginTop:8 }}>Laundry Service Receipt</div>
              </div>
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:C.sub, marginBottom:14 }}>
                <span>Receipt No: <strong>LDY-{Math.floor(Math.random()*9000)+1000}</strong></span>
                <span>Date: <strong>{new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</strong></span>
              </div>
              <div style={{ background:C.cream, borderRadius:8, padding:'12px 16px', display:'flex', justifyContent:'space-between', marginBottom:16, fontSize:13 }}>
                <div>
                  <div style={{ fontWeight:700, color:C.navy }}>{showReceipt.student.name}</div>
                  <div style={{ color:C.sub, marginTop:2 }}>ID: {showReceipt.student.admissionNumber || showReceipt.student.studentId}</div>
                </div>
                <div style={{ textAlign:'right' }}>
                  <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub }}>Balance After</div>
                  <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:20, fontWeight:700, color:C.navy }}>₹{Number(showReceipt.newBal).toLocaleString('en-IN')}</div>
                </div>
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:14 }}>
                <thead>
                  <tr style={{ background:C.navy }}>
                    {['Item','Qty','Rate','Amount'].map(h => (
                      <th key={h} style={{ padding:'8px 10px', textAlign: h === 'Amount' ? 'right' : 'left', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'white' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {showReceipt.items.map((r, i) => (
                <tr key={i} style={{ borderBottom:`1px solid ${C.light}` }}>
                  <td style={{ padding:'9px 10px', fontSize:13 }}>{r.name}</td>
                  <td style={{ padding:'9px 10px', fontSize:13 }}>{r.qty}</td>
                  <td style={{ padding:'9px 10px', fontSize:13 }}>₹{r.price || 0}</td>
                  <td style={{ padding:'9px 10px', fontSize:13, textAlign:'right' }}>₹{(r.qty * (r.price || 0)).toLocaleString('en-IN')}</td>
                </tr>
              ))}
                </tbody>
              </table>
              <div style={{ display:'flex', justifyContent:'space-between', fontFamily:'Cormorant Garamond,serif', fontSize:18, fontWeight:700, color:C.navy, paddingTop:10, borderTop:`2px solid ${C.navy}`, marginBottom:20 }}>
                <span>Total Charged</span><span>₹{showReceipt.total.toLocaleString('en-IN')}</span>
              </div>
              <div style={{ textAlign:'center', fontSize:11, color:C.sub, borderTop:`1px solid ${C.light}`, paddingTop:10 }}>
                Computer-generated receipt. No stamp required. · Omkara Hostel
              </div>
            </div>
            <div style={{ padding:'16px 24px', background:C.cream, borderTop:`1px solid ${C.light}`, display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button onClick={() => setShowReceipt(null)} style={{ padding:'10px 20px', background:C.light, color:C.text, border:'none', borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, cursor:'pointer' }}>Close</button>
              <button onClick={() => window.print()} style={{ padding:'10px 20px', background:C.navy, color:'white', border:'none', borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, cursor:'pointer' }}>🖨 Print</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const OtherChargesModule = ({ student, setStudent, showToast, refreshKey  }) => {
  const [items, setItems] = React.useState([]);
  const [qty, setQty]     = React.useState({});   // { itemId: number }
  const [chargeResult, setChargeResult] = React.useState('');
  const [busy, setBusy]   = React.useState(false);
 
  React.useEffect(() => {
    payCardOpsService.getOtherChargeItems()
      .then(setItems)
      .catch(() => showToast('Could not load items'));
  }, [refreshKey]);
 
  const setItemQty = (id, v) => {
    const n = Math.max(0, parseInt(v || '0', 10) || 0);
    setQty(p => ({ ...p, [id]: n }));
  };
 
  const cart  = items.filter(i => (qty[i.id] || 0) > 0)
                     .map(i => ({ name: i.name, price: i.price, qty: qty[i.id] }));
  const total = cart.reduce((s, i) => s + i.price * i.qty, 0);
 
 /* const handleScanStudent = async (idStr) => {
    if (!idStr || !idStr.trim()) return;
    try {
      const data = await payCardOpsService.lookupStudent(idStr.trim());
      if (data.cardStatus === 'BLOCKED') { showToast('🚫 Pay Card suspended — recharge first.'); return; }
      setStudent(data);
    } catch { showToast('Student not found'); }
  }; */

  const handleScanStudent = async (idStr) => {
    if (!idStr || !idStr.trim()) return;
    try {
      const data = await payCardOpsService.lookupStudent(idStr.trim());
      if (data.cardStatus === 'NOT_ISSUED') { showToast('❌ No pay card issued for this student'); return; }
      if (data.cardStatus === 'INACTIVE')  { showToast('⏳ Pay Card fee payment pending. Student must pay prepaid card fee to activate.'); return; }
      if (data.cardStatus === 'BLOCKED')   { showToast('🚫 Pay Card is suspended — balance is ₹0. Please recharge the wallet first.'); return; }
      setStudent({
        id: data.payCardId, payCardId: data.payCardId,
        admissionId: data.admissionId, studentId: data.studentId,
        admissionNumber: data.admissionNumber,
        name: data.studentName, roomCode: data.bedCode,
        balance: data.balance, status: data.status,
      });
    } catch { showToast('Student not found'); }
  };
 
  const charge = async () => {
    if (!student || total === 0) return;
    setBusy(true);
    try {
      const res = await payCardOpsService.chargeOther(student.payCardId, cart);
      setChargeResult(`₹${total.toLocaleString('en-IN')} charged. Balance: ₹${(res.balanceAfter ?? 0).toLocaleString('en-IN')}`);
      showToast(`✓ ₹${total.toLocaleString('en-IN')} charged to ${student.name}`);
      setQty({});
      setStudent(prev => prev ? { ...prev, balance: res.balanceAfter } : prev);
    } catch (e) {
      showToast(e?.response?.data?.message || 'Charge failed');
    } finally { setBusy(false); }
  };
 
  return (
    <div style={{ display:'grid', gridTemplateColumns:'340px 1fr', gap:24, alignItems:'start' }}>
      <ScanCard module="other" student={student}
        onScan={handleScanStudent}
        onClear={() => { setStudent(null); setChargeResult(''); }}
        chargeResult={chargeResult} />
 
      <div style={{ background:'white', borderRadius:14, boxShadow:'0 4px 20px rgba(0,0,0,0.08)', overflow:'hidden' }}>
        <div style={{ background:C.navy, color:'white', padding:'22px 26px' }}>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22 }}>Other Charges</div>
          <div style={{ fontSize:12, opacity:0.7, marginTop:3 }}>Guest stay, replacements, printing — enter quantity for each</div>
        </div>
 
        <div style={{ padding:'20px 26px' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ textAlign:'left', color:C.sub, fontSize:12, textTransform:'uppercase', letterSpacing:1 }}>
                <th style={{ padding:'8px 6px' }}>Item</th>
                <th style={{ padding:'8px 6px', textAlign:'right' }}>Rate</th>
                <th style={{ padding:'8px 6px', width:110, textAlign:'center' }}>Qty</th>
                <th style={{ padding:'8px 6px', textAlign:'right' }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map(i => {
                const q = qty[i.id] || 0;
                return (
                  <tr key={i.id} style={{ borderTop:`1px solid ${C.light}` }}>
                    <td style={{ padding:'10px 6px', fontSize:14 }}>{i.emoji ? i.emoji + ' ' : ''}{i.name}</td>
                    <td style={{ padding:'10px 6px', textAlign:'right', fontSize:14 }}>₹{i.price}</td>
                    <td style={{ padding:'10px 6px', textAlign:'center' }}>
                      <input type="number" min="0" value={q}
                        onChange={e => setItemQty(i.id, e.target.value)}
                        style={{ width:70, padding:'6px 8px', border:`2px solid ${C.light}`, borderRadius:6, textAlign:'center', fontFamily:'Work Sans,sans-serif' }} />
                    </td>
                    <td style={{ padding:'10px 6px', textAlign:'right', fontSize:14, fontWeight:700, color: q>0?C.navy:C.mid }}>
                      ₹{(i.price * q).toLocaleString('en-IN')}
                    </td>
                  </tr>
                );
              })}
              {items.length === 0 && (
                <tr><td colSpan={4} style={{ padding:20, textAlign:'center', color:C.sub }}>No items yet. Add them in “Manage Rates &amp; Items”.</td></tr>
              )}
            </tbody>
          </table>
 
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:20, paddingTop:16, borderTop:`2px solid ${C.light}` }}>
            <div style={{ fontSize:15, color:C.sub }}>Total</div>
            <div style={{ fontSize:22, fontWeight:700, color:C.navy }}>₹{total.toLocaleString('en-IN')}</div>
          </div>
 
          <button onClick={charge} disabled={!student || total === 0 || busy}
            style={btnCharge(!student || total === 0 || busy)}>
            {busy ? 'Charging…' : 'Charge Wallet'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─── ELECTRICITY MODULE ─────────────────────────────────────────────────── */

/* Room data fetched live from API — no hardcoded master */

const ElectricityModule = ({ showToast }) => {
  /* ── Selectors state ──────────────────────────────────────────────── */
  const [tower,        setTower]        = useState('');
  const [floor,        setFloor]        = useState('');
  const [wing,         setWing]         = useState('');
  const [roomCode,     setRoomCode]     = useState('');
  const [billingMonth, setBillingMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
  });
  const [totalBillInput, setTotalBillInput] = useState('');

  /* ── Dynamic room list from API ───────────────────────────────────── */
  const [floorData,    setFloorData]    = useState(null);  // { frontRooms, rearRooms }
  const [floorLoading, setFloorLoading] = useState(false);

  /* ── Panel state ──────────────────────────────────────────────────── */
  const [bedCards,      setBedCards]      = useState([]);
  const [billSaved,     setBillSaved]     = useState(false);
  const [bedRows,       setBedRows]       = useState([]);
  const [summaryData,   setSummaryData]   = useState(null);
  const [loading,       setLoading]       = useState(false);
  const [showReceipt,   setShowReceipt]   = useState(null);

  /* ── Billing history state ────────────────────────────────────────── */
  const [history,      setHistory]      = useState([]);
  const [histLoading,  setHistLoading]  = useState(false);
  const [histRoomCode, setHistRoomCode] = useState('');

  /* ── Auto-load recent history on mount + poll every 30s ─────────── */
  React.useEffect(() => {
    const loadRecent = async () => {
      setHistLoading(true);
      try {
        const data = await payCardOpsService.getElectricityHistory('');
        setHistory(data || []);
      // Poll every 30 seconds to keep history fresh
      const interval = setInterval(async () => {
        try {
          const fresh = await payCardOpsService.getElectricityHistory(histRoomCode?.trim().toUpperCase() || '');
          setHistory(fresh || []);
        } catch(_) {}
      }, 30000);
      return () => clearInterval(interval);
      } catch(e) { /* silent */ }
      finally { setHistLoading(false); }
    };
    loadRecent();
  }, []);

  /* ── Duplicate warning ────────────────────────────────────────────── */
  const [dupWarning, setDupWarning] = useState(null);
  React.useEffect(() => {
    setDupWarning(null);
    if (!roomCode || !billingMonth) return;
    const t = setTimeout(async () => {
      try {
        const res = await payCardOpsService.checkDuplicateBill(roomCode, billingMonth);
        if (res.alreadyBilled) setDupWarning(res.message);
      } catch(e) {}
    }, 600);
    return () => clearTimeout(t);
  }, [roomCode, billingMonth]);

  /* ── Fetch floor data when tower+floor selected ───────────────────── */
  React.useEffect(() => {
    if (!tower || !floor) { setFloorData(null); return; }
    setFloorLoading(true);
    roomService.getFloor(tower, parseInt(floor))
      .then(data => setFloorData(data))
      .catch(() => setFloorData(null))
      .finally(() => setFloorLoading(false));
  }, [tower, floor]);

  /* ── Derived wing options from API data ───────────────────────────── */
  const wingOptions = React.useMemo(() => {
    if (!floorData) return [];
    const opts = [];
    if (floorData.rearRooms  && floorData.rearRooms.length  > 0) opts.push({ val: 'Rear',  label: 'Rear Wing'  });
    if (floorData.frontRooms && floorData.frontRooms.length > 0) opts.push({ val: 'Front', label: 'Front Wing' });
    return opts;
  }, [floorData]);

  /* ── Derived room options from selected wing ──────────────────────── */
  const roomOptions = React.useMemo(() => {
    if (!floorData || !wing) return [];
    const rooms = wing === 'Rear' ? (floorData.rearRooms || []) : (floorData.frontRooms || []);
    return rooms.map(r => ({
      val:   r.roomCode,
      label: `${r.roomCode}  (${r.capacity || '?'} beds)`,
    }));
  }, [floorData, wing]);

  /* ── Floor list (1-7, static) ─────────────────────────────────────── */
  const floorOptions = tower
    ? [1,2,3,4,5,6,7].map(f => ({ val: String(f), label: `Floor ${f}${f===1?' (Ground)':''}` }))
    : [];

  /* ── Reset panel when selectors change ───────────────────────────── */
  const resetPanel = () => {
    setBedCards([]); setBillSaved(false); setBedRows([]);
    setSummaryData(null); setTotalBillInput(''); setDupWarning(null);
  };

  /* ── Load room beds from API ──────────────────────────────────────── */
  const loadRoomBeds = async (rc) => {
    if (!rc) return;
    setLoading(true);
    try {
      const data = await payCardOpsService.getRoomBeds(rc);
      setBedCards(data || []);
    } catch(e) {
      showToast('❌ Failed to load room: ' + (e.response?.data?.message || e.message));
      setBedCards([]);
    } finally { setLoading(false); }
  };

  /* ── "Save Bill" ──────────────────────────────────────────────────── */
  const saveBill = () => {
    if (!roomCode) { showToast('❌ Select a room first'); return; }
    const total = parseFloat(totalBillInput);
    if (!total || total <= 0) { showToast('❌ Enter a valid bill amount'); return; }

    const occupied = bedCards.filter(b => b.occupied);
    if (occupied.length === 0) { showToast('❌ No occupied beds in this room'); return; }
    const autoShare = Math.round(total / occupied.length);

    setSummaryData({
      room: roomCode,
      occupiedCount: occupied.length,
      totalBeds: bedCards.length,
      total,
      perBed: autoShare,
    });

    setBedRows(bedCards.map(b => ({
      bedCode:   b.bedCode,
      occupied:  b.occupied,
      name:      b.studentName || 'Unknown',
      balance:   b.balance || 0,
      payCardId: b.payCardId,
      assigned:  b.occupied ? autoShare : 0,
      deducted:  false,
    })));

    setBillSaved(true);
    showToast('✓ Bill saved. Adjust per-bed amounts if needed, then deduct.');
  };

  /* ── Update per-bed assigned amount ──────────────────────────────── */
  const updateAssigned = (bedCode, val) =>
    setBedRows(rs => rs.map(r => r.bedCode === bedCode ? { ...r, assigned: parseInt(val) || 0 } : r));

  /* ── Deduct single bed ────────────────────────────────────────────── */
  const deductBed = async (bedCode) => {
    const bed = bedRows.find(r => r.bedCode === bedCode);
    if (!bed || !bed.occupied || bed.deducted) return;
    if (!bed.payCardId) { showToast(`❌ No pay card for ${bed.name}`); return; }
    if (bed.assigned <= 0) { showToast('❌ Amount must be > 0'); return; }
    if (bed.balance < bed.assigned) { showToast(`❌ Insufficient balance for ${bed.name}`); return; }
    try {
      const res = await payCardOpsService.chargeElectricityBed(
        bed.payCardId, bed.assigned, roomCode, billingMonth
      );
      setBedRows(rs => rs.map(r => r.bedCode === bedCode
        ? { ...r, balance: (res && res.balanceAfter != null ? res.balanceAfter : r.balance - bed.assigned), deducted: true } : r));
      showToast(`✓ ₹${bed.assigned.toLocaleString('en-IN')} deducted from ${bed.name}`);
      loadHistory(''); // ← refresh history automatically
    } catch(e) {
      showToast(`❌ ${e.response?.data?.message || e.message}`);
    }
  };

  /* ── Deduct all ───────────────────────────────────────────────────── */
  const deductAll = async () => {
    const pending = bedRows.filter(b => b.occupied && !b.deducted && b.payCardId && b.assigned > 0 && b.balance >= b.assigned);
    const lowBal  = bedRows.filter(b => b.occupied && !b.deducted && b.balance < b.assigned);
    if (pending.length === 0) { showToast('❌ No eligible beds to deduct'); return; }
    if (lowBal.length > 0) showToast(`⚠ ${lowBal.length} bed(s) skipped — low balance`);

    const charges = pending.map(b => ({ payCardId: b.payCardId, bedCode: b.bedCode, amount: b.assigned }));
    try {
      await payCardOpsService.chargeElectricityBulk({
        roomCode, billingMonth,
        totalBill: parseFloat(totalBillInput),
        charges,
        operatorName: 'Staff',
      });
      const deductedSet = new Set(charges.map(c => c.bedCode));
      const newRows = bedRows.map(r => deductedSet.has(r.bedCode)
        ? { ...r, balance: r.balance - r.assigned, deducted: true } : r);
      setBedRows(newRows);
      showToast(`✓ ${charges.length} bed${charges.length !== 1 ? 's' : ''} deducted successfully`);
      loadHistory('');
      setShowReceipt({
        roomCode, billingMonth,
        totalBill: parseFloat(totalBillInput),
        beds: newRows.filter(r => deductedSet.has(r.bedCode)),
        date: new Date(),
      });
    } catch(e) {
      showToast(`❌ Bulk deduct failed: ${e.response?.data?.message || e.message}`);
    }
  };

  /* ── Billing history ──────────────────────────────────────────────── */
  const loadHistory = async (roomCodeOverride) => {
    const code = roomCodeOverride !== undefined ? roomCodeOverride : histRoomCode.trim().toUpperCase();
    setHistLoading(true);
    try {
      const data = await payCardOpsService.getElectricityHistory(code);
      setHistory(data || []);
    } catch(e) { showToast('❌ Failed to load history'); }
    finally { setHistLoading(false); }
  };

  /* ── Computed ─────────────────────────────────────────────────────── */
  const occupiedRows  = bedRows.filter(r => r.occupied);
  const assignedTotal = bedRows.reduce((s, r) => s + (r.assigned || 0), 0);
  const billTotal     = parseFloat(totalBillInput) || 0;
  const diff          = billTotal - assignedTotal;
  const allDeducted   = billSaved && occupiedRows.length > 0 && occupiedRows.every(r => r.deducted);

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:28 }}>

      {/* ── MAIN BILLING CARD ──────────────────────────────────────────── */}
      <div style={card}>
        <div style={{ padding:'22px 28px', borderBottom:`2px solid ${C.light}`, display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:22 }}>⚡</span>
          <span style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, fontWeight:600, color:C.navy }}>
            Electricity Bill — Shared Deduction
          </span>
        </div>

        <div style={{ padding:28 }}>
          <p style={{ fontSize:13, color:C.sub, marginBottom:24, lineHeight:1.7 }}>
            Select Tower → Floor → Wing → Room. The bill is split equally across <strong>occupied beds only</strong>.
            You can adjust per-bed amounts before deducting.
          </p>

          {/* ── Duplicate warning ────────────────────────────────────── */}
          {dupWarning && (
            <div style={{ background:'#FFF3CD', border:'1.5px solid #F59E0B', borderRadius:8,
              padding:'12px 18px', marginBottom:20, display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:20 }}>⚠️</span>
              <div>
                <div style={{ fontWeight:700, color:'#856404', fontSize:13 }}>Duplicate Bill Warning</div>
                <div style={{ color:'#856404', fontSize:12, marginTop:2 }}>{dupWarning} — Verify before proceeding.</div>
              </div>
            </div>
          )}

          {/* ── Selector grid: Tower / Floor / Wing / Room / Month / Bill ── */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:24 }}>

            {/* Tower */}
            <div>
              <label style={lbl}>Tower</label>
              <select
                value={tower}
                onChange={e => { setTower(e.target.value); setFloor(''); setWing(''); setRoomCode(''); setFloorData(null); resetPanel(); }}
                style={{ ...inp, cursor:'pointer' }}>
                <option value="">— Select Tower —</option>
                <option value="A">Tower A</option>
                <option value="B">Tower B</option>
              </select>
            </div>

            {/* Floor */}
            <div>
              <label style={lbl}>Floor</label>
              <select
                value={floor}
                onChange={e => { setFloor(e.target.value); setWing(''); setRoomCode(''); setFloorData(null); resetPanel(); }}
                disabled={!tower}
                style={{ ...inp, cursor: tower ? 'pointer' : 'not-allowed', background: tower ? 'white' : C.cream }}>
                <option value="">— Select Floor —</option>
                {floorOptions.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
              </select>
            </div>

            {/* Wing */}
            <div>
              <label style={lbl}>Wing {floorLoading && <span style={{fontSize:11,color:C.sub,fontWeight:400}}>Loading…</span>}</label>
              <select
                value={wing}
                onChange={e => { setWing(e.target.value); setRoomCode(''); resetPanel(); }}
                disabled={!floorData || wingOptions.length === 0}
                style={{ ...inp, cursor: (floorData && wingOptions.length > 0) ? 'pointer' : 'not-allowed', background: (floorData && wingOptions.length > 0) ? 'white' : C.cream }}>
                <option value="">— Select Wing —</option>
                {wingOptions.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
              </select>
            </div>

            {/* Room */}
            <div>
              <label style={lbl}>Room</label>
              <select
                value={roomCode}
                onChange={e => {
                  setRoomCode(e.target.value);
                  resetPanel();
                  if (e.target.value) loadRoomBeds(e.target.value);
                }}
                disabled={!wing || roomOptions.length === 0}
                style={{ ...inp, cursor: (wing && roomOptions.length > 0) ? 'pointer' : 'not-allowed', background: (wing && roomOptions.length > 0) ? 'white' : C.cream }}>
                <option value="">— Select Room —</option>
                {roomOptions.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
              </select>
            </div>

            {/* Billing Month */}
            <div>
              <label style={lbl}>Billing Month</label>
              <input
                type="month"
                value={billingMonth}
                onChange={e => setBillingMonth(e.target.value)}
                style={{ ...inp }}
              />
            </div>

            {/* Total Bill + Save Bill button */}
            <div>
              <label style={lbl}>Total Bill Amount (₹)</label>
              <div style={{ display:'flex', gap:8, alignItems:'stretch' }}>
                <input
                  type="number"
                  value={totalBillInput}
                  onChange={e => { setTotalBillInput(e.target.value); setBillSaved(false); }}
                  placeholder="e.g. 2400"
                  disabled={!roomCode}
                  style={{ ...inp, flex:1, background: roomCode ? 'white' : C.cream,
                    cursor: roomCode ? 'text' : 'not-allowed' }}
                />
                {roomCode && (
                  <button
                    onClick={saveBill}
                    style={{ padding:'12px 16px', background:C.navy, color:'white', border:'none',
                      borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, fontSize:13,
                      cursor:'pointer', whiteSpace:'nowrap' }}>
                    Save Bill
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* ── Empty state ───────────────────────────────────────────── */}
          {!roomCode && (
            <div style={{ textAlign:'center', padding:'40px 0', color:C.sub }}>
              <div style={{ fontSize:48, opacity:0.3, marginBottom:12 }}>⚡</div>
              <p style={{ fontSize:13 }}>Select Tower → Floor → Wing → Room to begin</p>
            </div>
          )}

          {/* ── Bed preview cards (after room selected, before Save Bill) ── */}
          {roomCode && bedCards.length > 0 && (
            <div style={{ marginBottom: billSaved ? 24 : 0 }}>
              {/* Section divider */}
              <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1.5,
                color:C.sub, display:'flex', alignItems:'center', gap:14, margin:'0 0 16px' }}>
                <div style={{ flex:1, height:1, background:C.light }} />
                Beds in <span style={{ fontStyle:'italic', color:C.navy }}>{roomCode}</span>
                <div style={{ flex:1, height:1, background:C.light }} />
              </div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:10, marginBottom:4 }}>
                {bedCards.map(b => (
                  <div key={b.bedCode}
                    style={{ border:`2px solid ${b.occupied ? C.green : C.light}`,
                      borderRadius:10, padding:'12px 16px', minWidth:140,
                      background: b.occupied ? '#f0faf6' : C.cream,
                      opacity: b.occupied ? 1 : 0.7 }}>
                    <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase',
                      letterSpacing:1, color:C.sub, marginBottom:4 }}>{b.bedCode}</div>
                    {b.occupied ? (
                      <>
                        <div style={{ fontWeight:700, color:C.text, fontSize:13 }}>{b.studentName}</div>
                        <div style={{ fontSize:11, color:C.green, marginTop:2 }}>
                          ₹{(b.balance||0).toLocaleString('en-IN')} balance
                        </div>
                      </>
                    ) : (
                      <div style={{ fontStyle:'italic', color:C.mid, fontSize:12 }}>Vacant</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {loading && (
            <div style={{ textAlign:'center', padding:'20px 0', color:C.sub, fontSize:13 }}>Loading room data…</div>
          )}

          {/* ── After Save Bill: Summary strip ────────────────────────── */}
          {billSaved && summaryData && (
            <div style={{ background:C.navy, color:'white', borderRadius:12, padding:24, marginBottom:24 }}>
              <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, fontWeight:400,
                marginBottom:16, opacity:0.85 }}>Billing Summary</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:20 }}>
                {[
                  ['Room',              summaryData.room],
                  ['Occupied Beds',     `${summaryData.occupiedCount} / ${summaryData.totalBeds}`],
                  ['Total Bill',        `₹${summaryData.total.toLocaleString('en-IN')}`],
                  ['Auto-split per Bed',`₹${summaryData.perBed.toLocaleString('en-IN')}`],
                ].map(([label, val]) => (
                  <div key={label} style={{ borderLeft:'3px solid rgba(255,255,255,0.3)', paddingLeft:14 }}>
                    <div style={{ fontSize:11, opacity:0.7, textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>{label}</div>
                    <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, fontWeight:700 }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── Per-bed editable table ─────────────────────────────────── */}
          {billSaved && bedRows.length > 0 && (
            <>
              {/* Divider */}
              <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1.5,
                color:C.sub, display:'flex', alignItems:'center', gap:14, margin:'0 0 16px' }}>
                <div style={{ flex:1, height:1, background:C.light }} />
                Per-Bed Amounts — Edit if needed
                <div style={{ flex:1, height:1, background:C.light }} />
              </div>

              <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:8 }}>
                <thead>
                  <tr style={{ background:C.navy }}>
                    {['Bed ID','Student Name','Current Balance','Amount to Deduct','Status','Action'].map(h => (
                      <th key={h} style={{ padding:'13px 16px', textAlign:'left', fontSize:12,
                        fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'white' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bedRows.map(b => {
                    const insuf  = b.occupied && b.balance < b.assigned;
                    const status = b.deducted ? 'deducted'
                      : !b.payCardId ? 'nocard'
                      : insuf ? 'low'
                      : 'ready';
                    return (
                      <tr key={b.bedCode} style={{ borderBottom:`1px solid ${C.light}`,
                        background: b.deducted ? '#F0FFF4' : 'white' }}>
                        <td style={{ padding:'14px 16px', fontWeight:700, fontSize:14 }}>{b.bedCode}</td>
                        <td style={{ padding:'14px 16px', fontSize:14 }}>
                          {b.occupied
                            ? <strong>{b.name}</strong>
                            : <em style={{ color:C.mid }}>Vacant</em>}
                        </td>
                        <td style={{ padding:'14px 16px', fontSize:14 }}>
                          {b.occupied
                            ? <span style={{ color: insuf ? C.red : C.text, fontWeight:600 }}>
                                ₹{b.balance.toLocaleString('en-IN')}
                              </span>
                            : '—'}
                        </td>
                        <td style={{ padding:'14px 16px' }}>
                          {b.occupied && !b.deducted ? (
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <span style={{ color:C.sub }}>₹</span>
                              <input
                                type="number"
                                value={b.assigned}
                                onChange={e => updateAssigned(b.bedCode, e.target.value)}
                                style={{ width:90, padding:'6px 10px', border:`2px solid ${C.light}`,
                                  borderRadius:6, fontFamily:'Work Sans,sans-serif',
                                  fontSize:14, fontWeight:700, textAlign:'right', outline:'none' }}
                              />
                            </div>
                          ) : b.occupied ? `₹${(b.assigned||0).toLocaleString('en-IN')}` : '—'}
                        </td>
                        <td style={{ padding:'14px 16px' }}>
                          {b.occupied ? (
                            <span style={{ padding:'5px 12px', borderRadius:20, fontSize:11, fontWeight:700,
                              background: status==='deducted' ? '#cce5ff'
                                : status==='low' ? '#FFF3CD'
                                : status==='nocard' ? C.light
                                : '#D4EDDA',
                              color: status==='deducted' ? '#004085'
                                : status==='low' ? '#856404'
                                : status==='nocard' ? C.sub
                                : '#155724' }}>
                              {status==='deducted' ? 'Deducted'
                                : status==='nocard' ? 'No Card'
                                : status==='low' ? 'Low Balance'
                                : 'Ready'}
                            </span>
                          ) : '—'}
                        </td>
                        <td style={{ padding:'14px 16px' }}>
                          {b.occupied && !b.deducted && b.payCardId ? (
                            <button onClick={() => deductBed(b.bedCode)}
                              style={{ padding:'8px 16px', background:C.navy, color:'white', border:'none',
                                borderRadius:6, fontFamily:'Work Sans,sans-serif',
                                fontSize:12, fontWeight:700, cursor:'pointer' }}>
                              Deduct
                            </button>
                          ) : b.occupied && b.deducted ? (
                            <span style={{ fontSize:13, fontWeight:700, color:'#27ae60' }}>✓ Done</span>
                          ) : b.occupied && !b.payCardId ? (
                            <span style={{ fontSize:11, color:C.mid }}>No card</span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Running total bar */}
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                background:C.cream, borderRadius:8, padding:'12px 18px', margin:'12px 0', fontSize:13 }}>
                <span style={{ color:C.sub }}>
                  Assigned total: <strong style={{ color:C.text }}>₹{assignedTotal.toLocaleString('en-IN')}</strong>
                </span>
                <span style={{ fontWeight:700,
                  color: Math.abs(diff) < 1 ? C.green : C.red }}>
                  {Math.abs(diff) < 1
                    ? '✓ Fully assigned'
                    : diff > 0
                    ? `Unassigned: ₹${diff.toLocaleString('en-IN')}`
                    : `Over by: ₹${Math.abs(diff).toLocaleString('en-IN')}`}
                </span>
              </div>

              <div style={{ display:'flex', justifyContent:'flex-end', gap:12 }}>
                {allDeducted ? (
                  <span style={{ color:C.green, fontWeight:700, fontSize:13, padding:'14px 0' }}>✓ All beds deducted</span>
                ) : (
                  <button
                    onClick={deductAll}
                    disabled={assignedTotal <= 0 || occupiedRows.every(r => r.deducted)}
                    style={{ padding:'14px 28px',
                      background: assignedTotal > 0 ? C.gold : C.mid,
                      color:'white', border:'none', borderRadius:8,
                      fontFamily:'Work Sans,sans-serif', fontWeight:700, fontSize:14,
                      textTransform:'uppercase', letterSpacing:1,
                      cursor: assignedTotal > 0 ? 'pointer' : 'not-allowed' }}>
                    ⚡ Deduct All
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── BILLING HISTORY CARD ───────────────────────────────────────── */}
      <div style={card}>
        <div style={{ padding:'22px 28px', borderBottom:`2px solid ${C.light}`, display:'flex', alignItems:'center', gap:12 }}>
          <span style={{ fontSize:22 }}>📋</span>
          <span style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, fontWeight:600, color:C.navy }}>
            Recent Electricity Bills
          </span>
        </div>
        <div style={{ padding:28 }}>
          {/* Room filter */}
          <div style={{ display:'flex', gap:10, marginBottom:20, alignItems:'flex-end' }}>
            <div>
              <label style={lbl}>Filter by Room Code <span style={{ fontWeight:400, color:C.sub }}>(leave blank for all recent)</span></label>
              <input
                value={histRoomCode}
                onChange={e => setHistRoomCode(e.target.value.toUpperCase())}
                onKeyDown={e => e.key === 'Enter' && loadHistory()}
                placeholder="e.g. AR-11 or leave blank"
                style={{ ...inp, width:220, textTransform:'uppercase' }}
              />
            </div>
            <button onClick={() => loadHistory()}
              style={{ padding:'12px 20px', background:C.navy, color:'white', border:'none',
                borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, fontSize:13,
                cursor:'pointer', height:46 }}>
              🔍 Search
            </button>
            <button onClick={() => { setHistRoomCode(''); loadHistory(''); }}
              style={{ padding:'12px 20px', background:'#6B6B6B', color:'white', border:'none',
                borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, fontSize:13,
                cursor:'pointer', height:46 }}>
              Show All
            </button>
          </div>

          {histLoading && (
            <div style={{ textAlign:'center', padding:'30px 0', color:C.sub }}>Loading…</div>
          )}

          {!histLoading && history.length === 0 && (
            <div style={{ padding:0 }}>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead>
                  <tr style={{ background:C.navy }}>
                    {['Month','Room','Total Bill','Occupied Beds','Per Bed','Status'].map(h => (
                      <th key={h} style={{ padding:'13px 16px', textAlign:'left', fontSize:12,
                        fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'white' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td colSpan={6} style={{ textAlign:'center', padding:'30px', color:C.sub, fontSize:13 }}>
                      No electricity bill records found
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {!histLoading && history.length > 0 && (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:C.navy }}>
                  {['Month','Room','Bed','Student','Amount Charged','Balance After','Operator','Date'].map(h => (
                    <th key={h} style={{ padding:'13px 16px', textAlign:'left', fontSize:12,
                      fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'white' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={h.transactionId} style={{ borderBottom:`1px solid ${C.light}`,
                    background: i%2===0 ? 'white' : C.cream }}>
                    <td style={{ padding:'14px 16px', fontWeight:700, fontSize:14, color:C.navy }}>
                      {h.billingMonth || '—'}
                    </td>
                    <td style={{ padding:'14px 16px', fontSize:14, fontWeight:600, color:'#1B3B5F', fontFamily:'monospace' }}>{h.roomCode || h.bedCode?.slice(0,-1) || '—'}</td>
                    <td style={{ padding:'14px 16px', fontSize:14 }}>{h.bedCode || '—'}</td>
                    <td style={{ padding:'14px 16px', fontSize:14 }}>{h.studentName}</td>
                    <td style={{ padding:'14px 16px' }}>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:6,
                        background:'#D4EDDA', color:'#155724',
                        padding:'5px 12px', borderRadius:20, fontSize:12, fontWeight:700 }}>
                        ₹{(h.amount||0).toLocaleString('en-IN')} each
                      </span>
                    </td>
                    <td style={{ padding:'14px 16px', fontFamily:'Cormorant Garamond,serif',
                      fontSize:17, fontWeight:700, color:C.navy }}>
                      ₹{(h.balanceAfter||0).toLocaleString('en-IN')}
                    </td>
                    <td style={{ padding:'14px 16px', fontSize:12, color:C.sub }}>{h.operatorName || '—'}</td>
                    <td style={{ padding:'14px 16px', fontSize:12, color:C.sub }}>
                      {h.createdAt ? new Date(h.createdAt).toLocaleDateString('en-IN',
                        { day:'2-digit', month:'short', year:'numeric' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── RECEIPT MODAL ─────────────────────────────────────────────── */}
      {showReceipt && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:1000,
          display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'white', borderRadius:14, maxWidth:580, width:'100%', overflow:'hidden' }}>

            <div id="elecPrintReceipt" style={{ padding:'30px 36px', fontFamily:'Work Sans,sans-serif' }}>
              {/* Header */}
              <div style={{ textAlign:'center', marginBottom:20, paddingBottom:16, borderBottom:`2px solid ${C.light}` }}>
                <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:26, letterSpacing:4,
                  color:C.navy, fontWeight:300 }}>OMKARA</div>
                <div style={{ fontSize:11, letterSpacing:2, textTransform:'uppercase', color:C.sub, marginTop:2 }}>
                  Hostel Portal — Ahmedabad
                </div>
                <div style={{ fontSize:13, fontWeight:700, textTransform:'uppercase', letterSpacing:1.5,
                  color:C.navy, marginTop:10 }}>Electricity Bill Receipt</div>
              </div>

              {/* Meta */}
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:C.sub, marginBottom:16 }}>
                <span>Receipt No: <strong>ELC-{Math.floor(Math.random()*9000)+1000}</strong></span>
                <span>Date: <strong>{showReceipt.date.toLocaleDateString('en-IN',
                  { day:'2-digit', month:'short', year:'numeric' })}</strong></span>
              </div>

              {/* Summary box */}
              <div style={{ background:C.navy, color:'white', borderRadius:8, padding:'12px 18px',
                display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:18, textAlign:'center' }}>
                {[['Room', showReceipt.roomCode],
                  ['Billing Month', showReceipt.billingMonth],
                  ['Total Bill', `₹${showReceipt.totalBill.toLocaleString('en-IN')}`]
                ].map(([l,v]) => (
                  <div key={l}>
                    <div style={{ fontSize:10, opacity:0.7, textTransform:'uppercase', letterSpacing:1, marginBottom:4 }}>{l}</div>
                    <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, fontWeight:700 }}>{v}</div>
                  </div>
                ))}
              </div>

              {/* Per-bed table */}
              <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:16 }}>
                <thead>
                  <tr style={{ background:C.navy }}>
                    {['Bed','Student','Amount Charged','Balance After'].map(h => (
                      <th key={h} style={{ padding:'9px 12px',
                        textAlign: h==='Amount Charged'||h==='Balance After' ? 'right' : 'left',
                        fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:'white' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {showReceipt.beds.map((b, i) => (
                    <tr key={b.bedCode} style={{ borderBottom:`1px solid ${C.light}`,
                      background: i%2===0 ? 'white' : C.cream }}>
                      <td style={{ padding:'10px 12px', fontSize:13, fontWeight:700 }}>{b.bedCode}</td>
                      <td style={{ padding:'10px 12px', fontSize:13 }}>{b.name}</td>
                      <td style={{ padding:'10px 12px', fontSize:13, textAlign:'right',
                        fontWeight:700, color:C.red }}>₹{b.assigned.toLocaleString('en-IN')}</td>
                      <td style={{ padding:'10px 12px', fontFamily:'Cormorant Garamond,serif',
                        fontSize:16, textAlign:'right', color:C.navy }}>₹{b.balance.toLocaleString('en-IN')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Total */}
              <div style={{ display:'flex', justifyContent:'space-between',
                fontFamily:'Cormorant Garamond,serif', fontSize:18, fontWeight:700, color:C.navy,
                paddingTop:12, borderTop:`2px solid ${C.navy}`, marginBottom:24 }}>
                <span>Total Charged</span>
                <span>₹{showReceipt.beds.reduce((s,b)=>s+b.assigned,0).toLocaleString('en-IN')}</span>
              </div>

              {/* Signature */}
              <div style={{ maxWidth:240, marginLeft:'auto', textAlign:'center' }}>
                <div style={{ height:40 }} />
                <div style={{ borderTop:`1.5px solid ${C.text}`, paddingTop:8 }}>
                  <div style={{ fontWeight:700, fontSize:12 }}>Authorised Signatory</div>
                  <div style={{ fontSize:11, color:C.sub, marginTop:2 }}>Omkara Hostel, Ahmedabad</div>
                </div>
              </div>

              <div style={{ textAlign:'center', fontSize:11, color:C.sub,
                marginTop:18, borderTop:`1px solid ${C.light}`, paddingTop:12 }}>
                This is a computer-generated receipt. No stamp required. • Omkara Hostel • Ahmedabad
              </div>
            </div>

            {/* Modal actions */}
            <div style={{ padding:'20px 30px', background:C.cream,
              borderTop:`1px solid ${C.light}`, display:'flex', gap:12, justifyContent:'flex-end' }}>
              <button onClick={() => setShowReceipt(null)}
                style={{ padding:'13px 24px', background:C.light, color:C.text, border:'none',
                  borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, cursor:'pointer' }}>
                Close
              </button>
              <button onClick={() => window.print()}
                style={{ padding:'13px 24px', background:C.navy, color:'white', border:'none',
                  borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, cursor:'pointer' }}>
                🖨 Print Receipt
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


/* ─── QR MANAGEMENT MODULE ──────────────────────────────────────────────── */


const QRPreview = ({ student, onRegen }) => {
  const [qrDataUrl, setQrDataUrl] = React.useState(null);
  const [qrError,   setQrError]   = React.useState(false);
  const printRef = React.useRef(null);

  React.useEffect(() => {
    if (!student?.qrToken) { setQrDataUrl(null); return; }
    setQrError(false);
    // Generate real QR code from the actual OMK|studentId|TOKEN string
    import('qrcode').then(QRCode => {
      QRCode.toDataURL(student.qrToken, {
        width:        240,
        margin:       2,
        errorCorrectionLevel: 'H',
        color: { dark: '#1B2A4A', light: '#FFFFFF' },
      }).then(url => setQrDataUrl(url))
        .catch(() => setQrError(true));
    }).catch(() => setQrError(true));
  }, [student?.qrToken]);

  const handlePrint = () => {
    const win = window.open('', '_blank', 'width=400,height=520');
    win.document.write(`
      <html><head><title>QR Card — ${student.name}</title>
      <style>
        body { font-family: 'Work Sans', sans-serif; margin:0; padding:24px; text-align:center; background:#fff; }
        .card { border:2px solid #1B2A4A; border-radius:12px; padding:24px; display:inline-block; }
        .hostel { font-size:13px; font-weight:700; letter-spacing:2px; color:#888; margin-bottom:4px; }
        .name { font-size:22px; font-weight:700; color:#1B2A4A; margin-bottom:2px; }
        .meta { font-size:12px; color:#888; margin-bottom:16px; }
        img { display:block; margin:0 auto 12px; border:1px solid #eee; border-radius:8px; }
        .token { font-size:10px; letter-spacing:1px; color:#aaa; font-family:monospace; }
      </style></head>
      <body><div class="card">
        <div class="hostel">OMKARA HOSTEL — PAY CARD</div>
        <div class="name">${student.name}</div>
        <div class="meta">Room: ${student.room} &nbsp;|&nbsp; Balance: ₹${(student.balance||0).toLocaleString('en-IN')}</div>
        <img src="${qrDataUrl}" width="200" height="200" />
        <div class="token">${student.qrToken}</div>
      </div>
      <script>window.onload=()=>{window.print();window.close();}<\/script>
      </body></html>
    `);
    win.document.close();
  };

  if (!student) return (
    <div style={{ textAlign:'center', padding:'48px 0', color:C.sub }}>
      <div style={{ fontSize:48, opacity:0.25, marginBottom:12 }}>⊞</div>
      <p style={{ fontSize:13 }}>Select a student from the left<br/>to preview their QR code</p>
    </div>
  );

  return (
    <div ref={printRef}>
      {/* Student info */}
      <div style={{ textAlign:'center', marginBottom:20 }}>
        <div style={{ fontSize:18, fontWeight:700, color:C.navy, marginBottom:4 }}>{student.name}</div>
        <div style={{ fontSize:13, color:C.sub }}>ID: {student.admissionNumber || student.studentId} · Room {student.room}</div>
      </div>

      {/* QR code — hidden if INACTIVE or BLOCKED */}
      {(() => {
        const st = (student.status || student.cardStatus || 'ACTIVE').toUpperCase();
        if (st === 'INACTIVE') return (
          <div style={{ background:'#FFFBEB', border:'2px dashed #F59E0B', borderRadius:12, padding:32, textAlign:'center', marginBottom:18 }}>
            <div style={{ fontSize:40, marginBottom:12 }}>⏳</div>
            <div style={{ fontWeight:700, color:'#92400E', fontSize:14, marginBottom:6 }}>Pay Card Fee Pending</div>
            <div style={{ fontSize:12, color:'#6B6B6B', lineHeight:1.6 }}>
              QR code will be activated once the<br/>prepaid card fee is paid by the student.
            </div>
          </div>
        );
        if (st === 'BLOCKED') return (
          <div style={{ background:'#FEF2F2', border:'2px dashed #EF4444', borderRadius:12, padding:32, textAlign:'center', marginBottom:18 }}>
            <div style={{ fontSize:40, marginBottom:12 }}>🚫</div>
            <div style={{ fontWeight:700, color:'#991B1B', fontSize:14, marginBottom:6 }}>Card Suspended — Zero Balance</div>
            <div style={{ fontSize:12, color:'#6B6B6B', lineHeight:1.6 }}>
              QR code is disabled.<br/>Please recharge the wallet to reactivate.
            </div>
          </div>
        );
        return (
          <div style={{ background:C.cream, border:`2px dashed ${C.mid}`, borderRadius:12, padding:24, textAlign:'center', marginBottom:18 }}>
            {qrDataUrl ? (
              <div style={{ display:'inline-block', background:'white', border:`2px solid ${C.light}`, borderRadius:8, padding:14, marginBottom:12 }}>
                <img src={qrDataUrl} alt="QR Code" width={200} height={200} style={{ display:'block', borderRadius:4 }} />
              </div>
            ) : qrError ? (
              <div style={{ padding:40, color:C.sub, fontSize:13 }}>
                ⚠ QR generation failed.<br/>
                <small>Run: <code>npm install qrcode</code></small>
              </div>
            ) : (
              <div style={{ padding:40, color:C.sub, fontSize:13 }}>Generating QR…</div>
            )}
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:2, color:C.sub, marginTop:6, fontFamily:'monospace' }}>
              {student.qrToken || '—'}
            </div>
          </div>
        );
      })()}

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:18 }}>
        <div style={{ background:C.cream, borderRadius:8, padding:14 }}>
          <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, marginBottom:6 }}>Wallet Balance</div>
          <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:24, fontWeight:700, color:C.navy }}>
            ₹{(student.balance || 0).toLocaleString('en-IN')}
          </div>
        </div>
        <div style={{ background:C.cream, borderRadius:8, padding:14 }}>
          <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, marginBottom:6 }}>Card Status</div>
          {(() => {
            const st = (student.status || student.cardStatus || 'ACTIVE').toUpperCase();
            const map = {
              ACTIVE:   { bg:'#D4EDDA', color:'#155724', label:'Active' },
              INACTIVE: { bg:'#FFF3CD', color:'#856404', label:'Fee Pending' },
              BLOCKED:  { bg:'#F8D7DA', color:'#721C24', label:'Suspended' },
            };
            const s = map[st] || map.ACTIVE;
            return <span style={{ padding:'4px 12px', borderRadius:20, fontSize:11, fontWeight:700, background:s.bg, color:s.color }}>{s.label}</span>;
          })()}
        </div>
      </div>

      {/* Actions — disable print when card not active */}
      <div style={{ display:'flex', gap:10 }}>
        <button
          onClick={handlePrint}
          disabled={!qrDataUrl}
          style={{ flex:1, padding:'13px', background: qrDataUrl ? C.navy : C.mid, color:'white', border:'none', borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, fontSize:13, cursor: qrDataUrl ? 'pointer' : 'not-allowed' }}>
          🖨 Print QR Card
        </button>
        <button
          onClick={() => onRegen(student)}
          style={{ padding:'13px 18px', background:C.cream, border:`2px solid ${C.light}`, borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, fontSize:13, cursor:'pointer', color:C.sub }}>
          Regenerate
        </button>
      </div>
    </div>
  );
};


const QRModule = ({ showToast }) => {
  const [selected,     setSelected]     = React.useState(null);
  const [regenConfirm, setRegenConfirm] = React.useState(null);
  const [students,     setStudents]     = React.useState([]);
  const [loading,      setLoading]      = React.useState(true);
  const [error,        setError]        = React.useState('');
  const [search,       setSearch]       = React.useState('');

  const loadStudents = () => {
    setLoading(true);
    setError('');
    payCardOpsService.getStudentQRList()
      .then(list => {
        if (list && list.length > 0) {
          setStudents(list.map(s => ({
            id:              s.payCardId,
            studentId:       s.studentId,
            admissionNumber: s.admissionNumber,
            admissionId:     s.admissionId,
            name:            s.studentName || 'Unknown',
            initials:        (s.studentName || 'UN').split(' ').map(w => w[0]).join('').slice(0,2).toUpperCase(),
            room:            s.roomCode || '—',
            balance:         parseFloat(s.balance) || 0,
            qrToken:         s.qrToken,
            status:          s.status || 'ACTIVE',
          })));
        } else {
          setStudents([]);
        }
      })
      .catch(e => {
        console.error('QR list error:', e);
        setError('Failed to load students. Is the backend running?');
      })
      .finally(() => setLoading(false));
  };

  React.useEffect(() => { loadStudents(); }, []);

  const handleRegen = (student) => setRegenConfirm(student);

  const confirmRegen = async () => {
    try {
      const res = await payCardOpsService.regenerateQR(regenConfirm.id);
      setStudents(prev => prev.map(s =>
        s.id === regenConfirm.id ? { ...s, qrToken: res.newQrToken } : s
      ));
      if (selected && selected.id === regenConfirm.id) {
        setSelected(s => ({ ...s, qrToken: res.newQrToken }));
      }
      showToast(`✓ New QR generated for ${regenConfirm.name}. Old QR invalidated.`);
    } catch(e) {
      showToast('❌ Failed to regenerate QR');
    }
    setRegenConfirm(null);
  };

  // Filter by name, room or student ID
  const filtered = students.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return s.name.toLowerCase().includes(q)
        || String(s.studentId).includes(q)
        || s.room.toLowerCase().includes(q);
  });

  return (
    <>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:24 }}>

        {/* Left — student list */}
        <div style={card}>
          <div style={{ padding:'20px 28px', borderBottom:`2px solid ${C.light}`, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <span style={{ fontSize:22 }}>⊞</span>
              <span style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, fontWeight:600, color:C.navy }}>Student QR Codes</span>
            </div>
            <button onClick={loadStudents} title="Refresh list"
              style={{ background:'none', border:`1.5px solid ${C.light}`, borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:16, color:C.sub }}>
              ↻
            </button>
          </div>

          {/* Search */}
          <div style={{ padding:'14px 28px', borderBottom:`1px solid ${C.light}` }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by name, room or student ID…"
              style={{ width:'100%', padding:'9px 14px', border:`1.5px solid ${C.light}`, borderRadius:8,
                fontFamily:'Work Sans,sans-serif', fontSize:13, outline:'none', boxSizing:'border-box' }}
            />
          </div>

          <div style={{ padding:'16px 28px' }}>
            <p style={{ fontSize:12, color:C.sub, marginBottom:16 }}>
              {loading ? 'Loading…' : `${filtered.length} student${filtered.length !== 1 ? 's' : ''} · Click Preview to view QR`}
            </p>

            {/* States */}
            {loading && (
              <div style={{ textAlign:'center', padding:'40px 0', color:C.sub, fontSize:14 }}>
                Loading students…
              </div>
            )}
            {!loading && error && (
              <div style={{ padding:'14px 16px', background:'#FFF3F3', border:'1px solid #FFCCCC',
                borderRadius:8, color:'#C0392B', fontSize:13, marginBottom:16 }}>
                {error}
                <button onClick={loadStudents}
                  style={{ marginLeft:12, background:'none', border:'none', cursor:'pointer',
                    color:C.navy, fontWeight:700, fontSize:13 }}>Retry</button>
              </div>
            )}
            {!loading && !error && students.length === 0 && (
              <div style={{ textAlign:'center', padding:'40px 0', color:C.sub, fontSize:13 }}>
                No pay cards found.<br/>
                <small>Cards are created automatically when admission is confirmed.</small>
              </div>
            )}
            {!loading && !error && students.length > 0 && filtered.length === 0 && (
              <div style={{ textAlign:'center', padding:'20px 0', color:C.sub, fontSize:13 }}>
                No students match "{search}"
              </div>
            )}

            {/* Student rows */}
            {!loading && filtered.map(s => (
              <div key={s.id}
                onClick={() => setSelected(s)}
                style={{ display:'flex', justifyContent:'space-between', alignItems:'center',
                  padding:'13px 12px', borderRadius:8, marginBottom:4, cursor:'pointer',
                  background: selected?.id === s.id ? C.cream : 'transparent',
                  border: `1.5px solid ${selected?.id === s.id ? C.mid : 'transparent'}`,
                  transition:'background 0.15s' }}>
                <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:38, height:38, borderRadius:8, background:C.navy,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontFamily:'Cormorant Garamond,serif', fontWeight:700, color:'white', fontSize:15 }}>
                    {s.initials}
                  </div>
                  <div>
                    <div style={{ fontSize:14, fontWeight:600, color:C.text }}>{s.name}</div>
                    <div style={{ fontSize:11, color:C.sub }}>
                      Student ID: {s.admissionNumber || s.studentId} &nbsp;·&nbsp; Room {s.room} &nbsp;·&nbsp;
                      ₹{s.balance.toLocaleString('en-IN')} &nbsp;·&nbsp;
                      <span style={{ color: s.status === 'ACTIVE' ? '#27ae60' : '#e74c3c' }}>
                        {s.status}
                      </span>
                    </div>
                  </div>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <button
                    onClick={e => { e.stopPropagation(); setSelected(s); }}
                    style={{ padding:'5px 12px', background: selected?.id === s.id ? C.navy : C.cream,
                      border:`2px solid ${C.light}`, borderRadius:6, cursor:'pointer',
                      fontSize:11, fontWeight:700,
                      color: selected?.id === s.id ? 'white' : C.sub }}>
                    Preview
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); handleRegen(s); }}
                    style={{ padding:'5px 12px', background:'white', border:`2px solid ${C.coral}`,
                      borderRadius:6, cursor:'pointer', fontSize:11, fontWeight:700, color:C.coral }}>
                    Regen
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right — QR preview */}
        <div style={card}>
          <div style={{ padding:'20px 28px', borderBottom:`2px solid ${C.light}`, display:'flex', alignItems:'center', gap:12 }}>
            <span style={{ fontSize:22 }}>🔳</span>
            <span style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, fontWeight:600, color:C.navy }}>QR Preview &amp; Print</span>
          </div>
          <div style={{ padding:28 }}>
            <QRPreview student={selected} onRegen={handleRegen} />
          </div>
        </div>
      </div>

      {/* Regen Confirm Modal */}
      {regenConfirm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:1000,
          display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div style={{ background:'white', borderRadius:14, maxWidth:440, width:'100%', padding:36 }}>
            <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:26, color:C.navy, marginBottom:8 }}>
              Regenerate QR Code?
            </div>
            <div style={{ color:C.sub, fontSize:14, marginBottom:16 }}>
              This will immediately invalidate the current QR for <strong>{regenConfirm.name}</strong>. They will need a new card printed.
            </div>
            <div style={{ background:C.cream, borderRadius:8, padding:'12px 16px', marginBottom:20, fontSize:13, color:C.sub }}>
              ⚠️ Any pending scan with the old QR will be rejected after regeneration.
            </div>
            <div style={{ display:'flex', gap:12 }}>
              <button onClick={() => setRegenConfirm(null)}
                style={{ flex:1, padding:13, background:C.light, color:C.text, border:'none',
                  borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={confirmRegen}
                style={{ flex:1, padding:13, background:C.navy, color:'white', border:'none',
                  borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, cursor:'pointer' }}>
                Yes, Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};


const RatesMgmtModal = ({ open, rates, setRates, canteenItems, setCanteenItems, laundryRows, setLaundryRows, onClose, showToast, onSaved  }) => {
  const [localRates, setLocalRates] = React.useState({ both: rates?.both || 20 });
  const [localItems, setLocalItems] = React.useState([...canteenItems]);
  const [localCats,  setLocalCats]  = React.useState(laundryRows.map(r => ({ ...r })));
  const [newItem,    setNewItem]    = React.useState({ name:'', price:'' });
const [newCat,     setNewCat]     = React.useState({ name:'', price:'' });

const [localOther, setLocalOther] = React.useState([]);
const [newOther,   setNewOther]   = React.useState({ name:'', price:'' });
  React.useEffect(() => {
    if (open) {
      setLocalRates({ ...rates });
      // Load laundry items (name + price) from the API
    payCardOpsService.getLaundryItems().then(list => {
      if (list && list.length > 0) {
        setLocalCats(list.map(i => ({ id: i.id, name: i.name, price: i.price })));
      } else {
        setLocalCats([]);
      }
    }).catch(() => setLocalCats([]));


      // Always fetch fresh canteen items from API when modal opens
      payCardOpsService.getCanteenItems().then(items => {
        if (items && items.length > 0) {
          setLocalItems(items.map(i => ({ id: i.id, name: i.name, emoji: i.emoji || '🍽', price: i.price })));
        } else {
          setLocalItems([...canteenItems]);
        }
      }).catch(() => setLocalItems([...canteenItems]));
     /* // Fetch fresh laundry rates
      payCardOpsService.getLaundryRates().then(r => {
        if (r) setLocalRates({ both: r.both || 20 });
      }).catch(() => {});*/

      // Fetch fresh laundry rates
      payCardOpsService.getLaundryRates().then(r => {
        if (r) setLocalRates({ both: r.both || 20 });
      }).catch(() => {});
      // Fetch fresh Other Charges items
      payCardOpsService.getOtherChargeItems().then(items => {
        setLocalOther((items || []).map(i => ({ id: i.id, name: i.name, emoji: i.emoji || '🧾', price: i.price })));
      }).catch(() => setLocalOther([]));

    }
  }, [open]);

  if (!open) return null;

  const save = async () => {
    // Flush pending new laundry item if user typed but didn't click + Add
  let finalCats = localCats;
  if (newCat.name.trim() && parseInt(newCat.price) > 0) {
    const flushedCat = { id: 'custom_' + Date.now(), name: newCat.name.trim(), price: parseInt(newCat.price) };
    finalCats = [...localCats, flushedCat];
    setLocalCats(finalCats);
    setNewCat({ name:'', price:'' });
  }

    // Flush any pending new canteen item that user typed but didn't click + Add
    let finalItems = localItems;
    if (newItem.name.trim() && parseInt(newItem.price) > 0) {
      const flushed = { id: 'custom_' + Date.now(), name: newItem.name.trim(), emoji: '🍽', price: parseInt(newItem.price) };
      finalItems = [...localItems, flushed];
      setLocalItems(finalItems);
      setNewItem({ name: '', price: '' });
    }
    try {
      // 1. Save laundry ITEMS with per-item prices
  const laundryPayload = finalCats.map(c => ({
    id:       (typeof c.id === 'number') ? c.id : null,
    name:     c.name,
    price:    Number(c.price) || 0,
    isActive: true,
  }));
  await payCardOpsService.saveAllLaundryItems(laundryPayload);

      // 2. Bulk save canteen items — PUT /paycards/canteen/items with full list
      // Map local items to backend CanteenItemDTO shape: { id, name, emoji, price, isActive }
      // Only send numeric DB ids — string ids (defaults like 'tea') and custom_ ids = new items
      const itemsPayload = finalItems.map(item => ({
        id:       (typeof item.id === 'number') ? item.id : null,
        name:     item.name,
        emoji:    item.emoji || '🍽',
        price:    Number(item.price) || 0,
        isActive: true,
      }));
      const savedItems = await payCardOpsService.saveAllCanteenItems(itemsPayload);
      const normalised = (savedItems || finalItems).map(i => ({
        id: i.id, name: i.name, emoji: i.emoji || '🍽', price: i.price
      }));
      //setCanteenItems(normalised);
      //setLocalItems(normalised);

       setCanteenItems(normalised);
      setLocalItems(normalised);
 
      // ── Other Charges: flush pending typed item, then save all ──
      let finalOther = localOther;
      if (newOther.name.trim() && parseInt(newOther.price) > 0) {
        finalOther = [...localOther, { id: 'custom_' + Date.now(), name: newOther.name.trim(), emoji: '🧾', price: parseInt(newOther.price) }];
        setLocalOther(finalOther);
        setNewOther({ name: '', price: '' });
      }
      // Delete items the user removed with the × (were in DB, now gone from list)
      try {
        const currentOther = await payCardOpsService.getOtherChargeItems();
        const keepIds = new Set(finalOther.filter(x => typeof x.id === 'number').map(x => x.id));
        for (const dbItem of (currentOther || [])) {
          if (!keepIds.has(dbItem.id)) await payCardOpsService.deleteOtherChargeItem(dbItem.id);
        }
      } catch (e) { /* non-fatal */ }
      // Add new items and update existing ones
      for (const it of finalOther) {
        const dto = { name: it.name, emoji: it.emoji || '🧾', price: Number(it.price) || 0, isActive: true };
        if (typeof it.id === 'number') {
          await payCardOpsService.updateOtherChargeItem(it.id, dto);
        } else {
          await payCardOpsService.addOtherChargeItem(dto);
        }
      }
    
    
    
    } catch(e) {
      console.error('Error saving rates/items:', e);
      showToast('⚠ Error saving — check console');
      return;
    }
    setRates(localRates);
    const savedCats = finalCats.map(cat => ({ ...cat, qty:0 }));
  setLaundryRows(savedCats);
    if (onSaved) onSaved();

    showToast('✓ Rates & items saved successfully');
    onClose();
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
      <div style={{ background:'white', borderRadius:14, maxWidth:680, width:'100%', maxHeight:'90vh', overflowY:'auto', padding:32 }}>
        <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:26, color:C.navy, marginBottom:6 }}>Manage Rates &amp; Items</div>
        <div style={{ color:C.sub, fontSize:14, marginBottom:24 }}>Update service rates and canteen menu. Changes reflect immediately across all charge screens.</div>

        

        {/* Laundry Items & Prices */}
    <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, marginBottom:12 }}>Laundry Items &amp; Prices</div>
    <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:12 }}>
      <thead><tr style={{ background:C.cream }}>
        <th style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, borderBottom:`2px solid ${C.light}` }}>Item Name</th>
        <th style={{ width:110, padding:'8px 12px', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, borderBottom:`2px solid ${C.light}` }}>Price (₹)</th>
        <th style={{ width:60, textAlign:'center', padding:'8px 12px', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, borderBottom:`2px solid ${C.light}` }}>Remove</th>
      </tr></thead>
      <tbody>
        {localCats.map(cat => (
          <tr key={cat.id} style={{ borderBottom:`1px solid ${C.light}` }}>
            <td style={{ padding:'9px 12px', fontSize:14 }}>{cat.name}</td>
            <td style={{ padding:'9px 12px' }}>
              <input type="number" value={cat.price} onChange={e => setLocalCats(cs => cs.map(c => c.id===cat.id ? {...c, price:parseInt(e.target.value)||c.price} : c))}
                style={{ width:80, padding:'5px 8px', border:`2px solid ${C.light}`, borderRadius:6, fontFamily:'Work Sans,sans-serif', fontSize:14, fontWeight:700, textAlign:'right', outline:'none' }} />
            </td>
            <td style={{ textAlign:'center', padding:'9px 12px' }}><button onClick={() => setLocalCats(cs => cs.filter(c => c.id !== cat.id))} style={{ background:'none', border:'none', cursor:'pointer', color:C.mid, fontSize:18 }}>×</button></td>
          </tr>
        ))}
      </tbody>
    </table>
    <div style={{ display:'flex', gap:10, marginBottom:24 }}>
      <input value={newCat.name} onChange={e => setNewCat(n => ({ ...n, name:e.target.value }))} placeholder="New item e.g. Blanket, Curtain..." style={{ ...inp, flex:1 }} />
      <input type="number" value={newCat.price} onChange={e => setNewCat(n => ({ ...n, price:e.target.value }))} placeholder="Price" style={{ ...inp, width:100 }} />
      <button onClick={() => { if(newCat.name.trim()&&parseInt(newCat.price)>0){ setLocalCats(cs=>[...cs,{id:'custom_'+Date.now(),name:newCat.name.trim(),price:parseInt(newCat.price)}]); setNewCat({name:'',price:''}); }}} style={{ padding:'10px 16px', background:C.navy, color:'white', border:'none', borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>+ Add</button>
    </div>



        <div style={{ height:1, background:C.light, margin:'0 0 20px' }} />

        {/* Canteen Menu */}
        <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, marginBottom:12 }}>Canteen Menu &amp; Prices</div>
        <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:12 }}>
          <thead><tr style={{ background:C.cream }}>
            <th style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, borderBottom:`2px solid ${C.light}` }}>Item Name</th>
            <th style={{ width:110, padding:'8px 12px', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, borderBottom:`2px solid ${C.light}` }}>Price (₹)</th>
            <th style={{ width:60, textAlign:'center', padding:'8px 12px', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, borderBottom:`2px solid ${C.light}` }}>Remove</th>
          </tr></thead>
          <tbody>
            {localItems.map(item => (
              <tr key={item.id} style={{ borderBottom:`1px solid ${C.light}` }}>
                <td style={{ padding:'9px 12px', fontSize:14 }}>{item.emoji} {item.name}</td>
                <td style={{ padding:'9px 12px' }}>
                  <input type="number" value={item.price} onChange={e => setLocalItems(its => its.map(i => i.id===item.id ? {...i, price:parseInt(e.target.value)||i.price} : i))}
                    style={{ width:80, padding:'5px 8px', border:`2px solid ${C.light}`, borderRadius:6, fontFamily:'Work Sans,sans-serif', fontSize:14, fontWeight:700, textAlign:'right', outline:'none' }} />
                </td>
                <td style={{ textAlign:'center', padding:'9px 12px' }}><button onClick={() => setLocalItems(its => its.filter(i => i.id !== item.id))} style={{ background:'none', border:'none', cursor:'pointer', color:C.mid, fontSize:18 }}>×</button></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display:'flex', gap:10, marginBottom:28 }}>
          <input value={newItem.name} onChange={e => setNewItem(n => ({...n, name:e.target.value}))} placeholder="New item name e.g. Poha, Upma..." style={{ ...inp, flex:1 }} />
          <input type="number" value={newItem.price} onChange={e => setNewItem(n => ({...n, price:e.target.value}))} placeholder="Price" style={{ ...inp, width:100 }} />
          <button onClick={() => { if(newItem.name.trim()&&parseInt(newItem.price)>0){ setLocalItems(its=>[...its,{id:'custom_'+Date.now(),name:newItem.name.trim(),emoji:'🍽',price:parseInt(newItem.price)}]); setNewItem({name:'',price:''}); }}} style={{ padding:'10px 16px', background:C.navy, color:'white', border:'none', borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>+ Add</button>
        </div>
<div style={{ height:1, background:C.light, margin:'0 0 20px' }} />
 
        {/* Other Charges */}
        <div style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, marginBottom:12 }}>Other Charges &amp; Rates</div>
        <table style={{ width:'100%', borderCollapse:'collapse', marginBottom:12 }}>
          <thead><tr style={{ background:C.cream }}>
            <th style={{ padding:'8px 12px', textAlign:'left', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, borderBottom:`2px solid ${C.light}` }}>Item Name</th>
            <th style={{ width:110, padding:'8px 12px', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, borderBottom:`2px solid ${C.light}` }}>Rate (₹)</th>
            <th style={{ width:60, textAlign:'center', padding:'8px 12px', fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1, color:C.sub, borderBottom:`2px solid ${C.light}` }}>Remove</th>
          </tr></thead>
          <tbody>
            {localOther.map(item => (
              <tr key={item.id} style={{ borderBottom:`1px solid ${C.light}` }}>
                <td style={{ padding:'9px 12px', fontSize:14 }}>{item.emoji} {item.name}</td>
                <td style={{ padding:'9px 12px' }}>
                  <input type="number" value={item.price} onChange={e => setLocalOther(its => its.map(i => i.id===item.id ? {...i, price:parseInt(e.target.value)||i.price} : i))}
                    style={{ width:80, padding:'5px 8px', border:`2px solid ${C.light}`, borderRadius:6, fontFamily:'Work Sans,sans-serif', fontSize:14, fontWeight:700, textAlign:'right', outline:'none' }} />
                </td>
                <td style={{ textAlign:'center', padding:'9px 12px' }}><button onClick={() => setLocalOther(its => its.filter(i => i.id !== item.id))} style={{ background:'none', border:'none', cursor:'pointer', color:C.mid, fontSize:18 }}>×</button></td>
              </tr>
            ))}
            {localOther.length === 0 && (
              <tr><td colSpan={3} style={{ padding:14, textAlign:'center', color:C.sub, fontSize:13 }}>No items yet — add below.</td></tr>
            )}
          </tbody>
        </table>
        <div style={{ display:'flex', gap:10, marginBottom:28 }}>
          <input value={newOther.name} onChange={e => setNewOther(n => ({...n, name:e.target.value}))} placeholder="New charge e.g. Missing Key, Print..." style={{ ...inp, flex:1 }} />
          <input type="number" value={newOther.price} onChange={e => setNewOther(n => ({...n, price:e.target.value}))} placeholder="Rate" style={{ ...inp, width:100 }} />
          <button onClick={() => { if(newOther.name.trim()&&parseInt(newOther.price)>0){ setLocalOther(its=>[...its,{id:'custom_'+Date.now(),name:newOther.name.trim(),emoji:'🧾',price:parseInt(newOther.price)}]); setNewOther({name:'',price:''}); }}} style={{ padding:'10px 16px', background:C.navy, color:'white', border:'none', borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}>+ Add</button>
        </div>
 
        <div style={{ display:'flex', gap:12, justifyContent:'flex-end' }}>
          <button onClick={onClose} style={{ padding:'12px 24px', background:C.light, color:C.text, border:'none', borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, cursor:'pointer' }}>Cancel</button>
          <button onClick={save} style={{ padding:'12px 24px', background:C.navy, color:'white', border:'none', borderRadius:8, fontFamily:'Work Sans,sans-serif', fontWeight:700, cursor:'pointer' }}>✓ Save All Changes</button>
        </div>
      </div>
    </div>
  );
};

/* ─── MAIN COMPONENT ─────────────────────────────────────────────────────── */
const PayCardOperations = ({ onNavigate, onLogout }) => {
  const [tab, setTab] = useState('canteen');
  const [student, setStudent] = useState(null);
  const [toast, setToast] = useState({ msg: '', visible: false });
  const toastTimer = useRef(null);
  const [canteenItems, setCanteenItems] = useState(DEFAULT_CANTEEN);

  // Load canteen items and laundry rates from API on mount
  useEffect(() => {
    payCardOpsService.getCanteenItems().then(items => {
      if (items && items.length > 0) setCanteenItems(items.map(i => ({ id: i.id, name: i.name, emoji: i.emoji || '🍽', price: i.price })));
    }).catch(() => {}); // fallback to defaults
    payCardOpsService.getLaundryRates().then(rates => {
      if (rates) setLaundryRates({ wash: rates.wash || 30, press: rates.press || 15, both: rates.both || 40 });
    }).catch(() => {});
  }, []);
  const [laundryRates, setLaundryRates] = useState({ wash:30, press:15, both:40 });
  const [laundryRows, setLaundryRows] = useState(() => {
    try {
      const saved = localStorage.getItem('laundryCategories');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed && parsed.length > 0) return parsed;
      }
    } catch(e) {}
    return LAUNDRY_DEFAULTS.map((name, i) => ({ id: i+1, name, qty:0, svc:null }));
  });
  const [ratesMgmtOpen, setRatesMgmtOpen] = useState(false);
    const [otherRefresh, setOtherRefresh] = useState(0);

  const showToast = useCallback((msg) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ msg, visible: true });
    toastTimer.current = setTimeout(() => setToast(t => ({ ...t, visible: false })), 3200);
  }, []);

  const tabs = [
    { id: 'canteen',     label: 'Canteen',            icon: '🍔' },
    { id: 'laundry',     label: 'Laundry',            icon: '👕' },
    { id: 'electricity', label: 'Electricity Billing', icon: '⚡' },
    { id: 'other',       label: 'Other Charges', icon: '🧾' },
    { id: 'qr',          label: 'QR Management',       icon: '⊞' },
  ];

  return (
    <div className="app-layout">
      <Sidebar activeItem="payCardOperations" onNavigate={onNavigate} onLogout={onLogout} />
      <main className="main-content">
        <div className="top-bar">
          <div>
            <div style={{ fontSize:13, color:C.sub, marginBottom:6 }}>
              <span style={{ color:C.gold, cursor:'pointer' }} onClick={() => onNavigate('finance')}>Finance</span>
              {' / '}Pay Card Operations
            </div>
            <h1 className="page-title">Pay Card Operations</h1>
          </div>
          <div style={{ fontSize:12, color:C.sub, textAlign:'right' }}>
            <div style={{ fontWeight:700, color:C.navy, fontSize:14 }}>{new Date().toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' })}</div>
            <div>Scan QR to charge wallet</div>
          </div>
        </div>

        <div className="content-area">
          {/* Module Tabs */}
          <div style={{ display:'flex', gap:10, marginBottom:32, flexWrap:'wrap', alignItems:'center' }}>
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={btnNav(tab === t.id)}>
                <span style={{ fontSize:18 }}>{t.icon}</span>{t.label}
              </button>
            ))}
            <button onClick={() => setRatesMgmtOpen(true)}
              style={{ ...btnNav(false), marginLeft:'auto', borderColor:C.gold, color:C.gold }}>
              ⚙ Settings 
            </button>
          </div>

          {tab === 'canteen'     && <CanteenModule     student={student} setStudent={setStudent} showToast={showToast} items={canteenItems} />}
          {tab === 'laundry'     && <LaundryModule     student={student} setStudent={setStudent} showToast={showToast} rates={laundryRates} initialRows={laundryRows} setGlobalRows={setLaundryRows} />}
          {tab === 'electricity' && <ElectricityModule showToast={showToast} />}
          {tab === 'other'       && <OtherChargesModule student={student} setStudent={setStudent} showToast={showToast} refreshKey={otherRefresh} />}          {tab === 'qr'          && <QRModule showToast={showToast} />}
          <RatesMgmtModal
            open={ratesMgmtOpen}
            rates={laundryRates} setRates={setLaundryRates}
            canteenItems={canteenItems} setCanteenItems={setCanteenItems}
            laundryRows={laundryRows} setLaundryRows={setLaundryRows}
            onClose={() => setRatesMgmtOpen(false)}
            showToast={showToast}
            onSaved={() => setOtherRefresh(n => n + 1)}

          />
        </div>
      </main>
      <Toast msg={toast.msg} visible={toast.visible} />
    </div>
  );
};

export default PayCardOperations;